import { Check, ChevronsUpDown, SearchIcon } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { OptionType } from "@/components/form-control/dropdown-select";
import { cn } from "@/lib/utils";
import { isEmpty } from "@/helper/utils";
import { useDebounce } from "@/hooks/lib-ui";

export type ComboboxProps = {
  options: OptionType[];
  placeholder?: string;
  className?: string;
  value?: string;
  onValueChange: (value: string) => void;
  inputClassName?: string;
};
export function Combobox({
  options,
  placeholder = "Search ...",
  className,
  value,
  onValueChange,
  inputClassName,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchString = useDebounce(searchValue, 500);

  const listOptions = useMemo(() => {
    if (isEmpty(searchString)) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(searchString.toLowerCase()));
  }, [options, searchString]);
  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between", className)}
        >
          <span className="truncate">{value ? options.find((item) => item.value === value)?.label : placeholder}</span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command>
          <div data-slot="command-input-wrapper" className="flex h-9 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 opacity-50" />
            <input
              data-slot="command-input"
              className={cn(
                "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                inputClassName,
              )}
              placeholder={placeholder ?? "Search"}
              value={searchValue}
              onChange={(ev) => setSearchValue(ev.target.value)}
            />
          </div>
          <CommandList>
            <CommandEmpty>No record found.</CommandEmpty>
            <CommandGroup>
              {listOptions.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentValue) => {
                    if (currentValue !== value) {
                      onValueChange(currentValue);
                    }
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  {item.label}
                  <Check className={cn("ml-auto", value === item.value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
